import { createRoot, hydrateRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'

// Handle Supabase auth tokens in URL hash
// Supabase redirects with: origin/#access_token=...&type=recovery
// With BrowserRouter, the hash fragment doesn't conflict with routing.
// We detect recovery tokens and store them for the Auth page to process.
const hash = window.location.hash;
if (hash && hash.includes('access_token') && hash.includes('type=recovery')) {
  const params = hash.substring(1); // remove leading #
  sessionStorage.setItem('supabase_recovery_params', params);
  // Clean the URL and redirect to auth page
  window.history.replaceState(null, '', '/auth?recovery=true');
}

// Recovery: a stale cached JS chunk can throw TDZ ("Cannot access 'X' before
// initialization") or chunk-load errors. Clear caches + SW and reload once.
const RECOVERY_FLAG = 'nf_bundle_recovery_at';

async function recoverFromStaleBundle(reason: string) {
  try {
    const last = Number(sessionStorage.getItem(RECOVERY_FLAG) || 0);
    if (Date.now() - last < 60_000) return; // only once per minute
    sessionStorage.setItem(RECOVERY_FLAG, String(Date.now()));
    console.warn('[recovery] clearing caches due to:', reason);
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    window.location.reload();
  } catch {
    // ignore - recovery is best effort
  }
}

const isStaleBundleError = (message: string) =>
  /before initialization|Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk .* failed/i.test(
    message
  );

window.addEventListener('error', (event) => {
  const message = event?.error?.message || event?.message || '';
  if (isStaleBundleError(String(message))) void recoverFromStaleBundle(String(message));
});

window.addEventListener('unhandledrejection', (event) => {
  const message = (event?.reason && (event.reason.message || String(event.reason))) || '';
  if (isStaleBundleError(String(message))) void recoverFromStaleBundle(String(message));
});

// Register Service Worker for caching and offline support
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { updateViaCache: 'none' })
      .then((registration) => {
        registration.update().catch(() => {});
        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          installing?.addEventListener('statechange', () => {
            if (installing.state === 'activated' && navigator.serviceWorker.controller) {
              // A newer worker took over: drop old asset caches on next load.
              console.info('SW updated');
            }
          });
        });
      })
      .catch(() => {
        // Service worker is an optional enhancement.
      });
  });
}

// If react-snap pre-rendered the page, hydrate it; otherwise render fresh.
const rootEl = document.getElementById("root")!;
if (rootEl.hasChildNodes()) {
  hydrateRoot(rootEl, <App />);
} else {
  createRoot(rootEl).render(<App />);
}
